import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { MdClose } from "react-icons/md";
import Swal from "sweetalert2";
import BtnLoader from "Components/Common/Loader/BtnLoader";
import { loadDecrypted } from "Common Usage/localStorageUtils";

// ── SweetAlert always above any modal ────────────────────────────────────────
const swalAbove = Swal.mixin({
  customClass: { container: "swal-above-modal" },
  didOpen: () => {
    const el = document.querySelector(".swal-above-modal");
    if (el) el.style.zIndex = "99999";
  },
});

const ManualRechargeForm = ({ onClose, refetch }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [searchDone, setSearchDone] = useState(false); // true after debounce fires

  const [rechargeType, setRechargeType] = useState("");
  const [utr, setUtr] = useState("");
  const [transactionId, setTransactionId] = useState("");
  const [coinPlan, setCoinPlan] = useState(null);
  const [coinPlanSearch, setCoinPlanSearch] = useState("");
  const [showCoinPlanDropdown, setShowCoinPlanDropdown] = useState(false);
  const [discount, setDiscount] = useState(null);
  const [discountSearch, setDiscountSearch] = useState("");
  const [showDiscountDropdown, setShowDiscountDropdown] = useState(false);
  const [method, setMethod] = useState("");
  const [customMethod, setCustomMethod] = useState("");
  const [errors, setErrors] = useState({});
  const [coinPlans, setCoinPlans] = useState([]);
  const [promoPlans, setPromoPlans] = useState([]);
  const [discountType, setDiscountType] = useState("");
  const [discountValue, setDiscountValue] = useState("");

  const loggedInUser = loadDecrypted("HeartConnect-StaffData");
  const staffData = Array.isArray(loggedInUser)
    ? loggedInUser[0]
    : loggedInUser;
  const rechargeBy = staffData?.fullName || "Unknown";
  const rechargeByUserId = staffData?._id || staffData?.id || "";

  const isAdminRole = (role) => {
    if (!role) return false;
    const r = role.trim().toLowerCase().replace(/[\s_]/g, "-");
    return r === "admin" || r === "super-admin";
  };
  const loggedInRoleName = staffData?.rolePermission?.rolename || "";
  const isLoggedInUserAdmin = isAdminRole(loggedInRoleName);
  const shouldShowWallet = !isLoggedInUserAdmin;

  // ── Staff wallet (admin-assigned coin balance) ────────────────────────────
  const [walletBalance, setWalletBalance] = useState(null);
  const [walletLoading, setWalletLoading] = useState(false);

  useEffect(() => {
    if (!rechargeByUserId || isLoggedInUserAdmin) {
      setWalletBalance(null);
      return;
    }
    const fetch = async () => {
      setWalletLoading(true);
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/staff-wallet/staffs-wallet-by-id/${rechargeByUserId}`
        );
        setWalletBalance(
          res.data?.data?.coinBalance ?? res.data?.data?.balance ?? 0
        );
      } catch {
        setWalletBalance(null);
      } finally {
        setWalletLoading(false);
      }
    };
    fetch();
  }, [rechargeByUserId, isLoggedInUserAdmin]);

  const [loading, setLoading] = useState(false);

  const ALL_METHODS = ["Netbanking", "UPI", "Card", "Wallet", "Promo", "Other"];
  const availableMethods = useMemo(() => {
    if (rechargeType === "manual") return ["Wallet", "Other"];
    if (rechargeType === "cashfree" || rechargeType === "cashfreefailed")
      return ALL_METHODS.filter((m) => m !== "Wallet");
    return ALL_METHODS;
  }, [rechargeType]);

  useEffect(() => {
    setMethod("");
    setCustomMethod("");
  }, [rechargeType]);

  // ── User search: debounce + exact-match only ──────────────────────────────

  useEffect(() => {
    if (searchQuery.trim().length === 0) {
      setSuggestions([]);
      setSearchDone(false);
      return;
    }
    setSearchDone(false);
    const handler = setTimeout(async () => {
      try {
        const rawQuery = searchQuery.trim();
        // Prepend 91 only if the query looks like a phone number (all digits)
        const searchParam = /^\d+$/.test(rawQuery) ? `91${rawQuery}` : rawQuery;

        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/users/get-name-number`,
          { params: { search: searchParam } }
        );
        const data = res.data.data || [];
        // Exact match: full name, nickname, or phone (match against original OR prefixed)
        const q = rawQuery.toLowerCase();
        const matched = data.filter(
          (u) =>
            u.fullName?.toLowerCase() === q ||
            u.nickName?.toLowerCase() === q ||
            u.phone === rawQuery ||
            u.phone === `91${rawQuery}`
        );
        setSuggestions(matched);
      } catch (err) {
        console.error("Error fetching users:", err);
        setSuggestions([]);
      } finally {
        setSearchDone(true);
      }
    }, 600);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    const fetchCoinPlans = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/coin-plan/getcoin-plans`
        );
        setCoinPlans(res.data || []);
      } catch (err) {
        console.error(err);
      }
    };
    const fetchPromoPlans = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/coin-plan-promo/getpromo-plans`
        );
        setPromoPlans(res.data || []);
      } catch (err) {
        console.error(err);
      }
    };
    fetchCoinPlans();
    fetchPromoPlans();
  }, []);

  const handleSelectSuggestion = (user) => {
    setSelectedUser(user);
    setErrors((p) => ({ ...p, user: "" }));
    setSearchQuery("");
    setSuggestions([]);
    setSearchDone(false);
  };

  const filteredCoinPlans = useMemo(() => {
    if (!coinPlanSearch) return coinPlans;
    return coinPlans.filter((p) => `${p.coinAmount}`.includes(coinPlanSearch));
  }, [coinPlanSearch, coinPlans]);

  const handleCoinPlanSelect = (plan) => {
    setCoinPlan(plan);
    setCoinPlanSearch(`${plan.coinAmount} Coins — ₹${plan.purchaseAmount}`);
    setShowCoinPlanDropdown(false);
    setErrors((p) => ({ ...p, coinPlan: "" }));
  };

  const filteredDiscounts = useMemo(() => {
    if (!discountSearch) return promoPlans;
    return promoPlans.filter((p) =>
      p.code.toLowerCase().includes(discountSearch.toLowerCase())
    );
  }, [discountSearch, promoPlans]);

  const handleDiscountSelect = (promo) => {
    setDiscount(promo);
    setDiscountSearch(`${promo.discount}% OFF`);
    setShowDiscountDropdown(false);
  };

  // ── Calculations ──────────────────────────────────────────────────────────
  // Purchase amount after rupee discount (goes to DB)
  const calculateFinalAmount = () => {
    if (!coinPlan) return 0;
    let amt = coinPlan.purchaseAmount;
    if (discount && discount.discount && !discountType)
      amt -= (amt * discount.discount) / 100;
    if (discountType === "percent" && discountValue > 0)
      amt -= (amt * Number(discountValue)) / 100;
    else if (discountType === "amount" && discountValue > 0)
      amt -= parseFloat(discountValue);
    return Math.max(0, parseFloat(amt.toFixed(2)));
  };

  // Coins credited to user = plan's coinAmount (fixed, not affected by rupee discount)
  const coinsToCredit = coinPlan ? coinPlan.coinAmount : 0;

  // Coins deducted from staff wallet = plan's coinAmount (same as credited)
  // e.g. plan = 50 coins → staff wallet: 1000 - 50 = 950
  const coinDeduction = coinPlan ? coinPlan.coinAmount : 0;

  const finalPurchaseAmount = calculateFinalAmount();
  const discountedRupees = coinPlan
    ? parseFloat((coinPlan.purchaseAmount - finalPurchaseAmount).toFixed(2))
    : 0;

  const remainingCoinBalance =
    walletBalance !== null ? walletBalance - coinDeduction : null;
  const isInsufficientBalance =
    walletBalance !== null &&
    coinDeduction > 0 &&
    coinDeduction > walletBalance;

  const validateForm = () => {
    const e = {};
    if (!selectedUser) e.user = "User is required";
    if (!rechargeType) e.rechargeType = "Recharge type is required";
    if (rechargeType === "manual" && !utr) e.utr = "UTR is required";
    if (
      (rechargeType === "cashfree" || rechargeType === "cashfreefailed") &&
      !transactionId
    )
      e.transactionId = "Transaction ID is required";
    if (!coinPlan) e.coinPlan = "Coin plan is required";
    if (!method) e.method = "Method is required";
    if (method === "Other" && !customMethod)
      e.customMethod = "Custom method is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleMakePayment = async (ev) => {
    ev.preventDefault();
    if (!validateForm()) return;

    const payload = {
      userID: selectedUser._id,
      coinPlan: coinPlan._id,
      promoCode: discount?._id,
      rechargeType,
      utr,
      transactionId,
      method: method === "Other" ? customMethod : method,
      discountType,
      discountValue,
      rechargeBy: rechargeByUserId,
      rechargeByName: rechargeBy,
      finalAmount: finalPurchaseAmount,
      staffId: shouldShowWallet ? rechargeByUserId : null,
    };

    try {
      setLoading(true);
      await axios.post(
        `${process.env.REACT_APP_API_URL}/transaction-history/manual-recharge`,
        payload
      );

      // Activity log (non-blocking)
      try {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/staffs-activity-log/create-staffs-activity`,
          {
            staff: rechargeByUserId,
            action: "MANUAL_RECHARGE",
            description:
              `Manual recharge for ${selectedUser.fullName} (${selectedUser._id}) | ` +
              `Plan: ${coinPlan.coinAmount} coins (₹${coinPlan.purchaseAmount}) | ` +
              `Final: ₹${finalPurchaseAmount} | Method: ${
                method === "Other" ? customMethod : method
              } | ` +
              `Type: ${rechargeType}` +
              (rechargeType === "manual" ? ` | UTR: ${utr}` : "") +
              (rechargeType === "cashfree" || rechargeType === "cashfreefailed"
                ? ` | TxnID: ${transactionId}`
                : ""),
          },
          { headers: { "Content-Type": "application/json" } }
        );
      } catch (logErr) {
        console.error("Activity log failed:", logErr);
      }

      setLoading(false);
      swalAbove.fire({
        icon: "success",
        title: "Recharge Successful",
        html: `
          <div style="text-align:left;font-size:14px;line-height:2;padding:4px 0">
            <div>👤 <b>${selectedUser.fullName}</b></div>
            <div>🪙 <b>${coinsToCredit.toLocaleString(
              "en-IN"
            )} Coins</b> credited</div>
            <div>💰 Paid: <b>₹${finalPurchaseAmount.toLocaleString(
              "en-IN"
            )}</b></div>
          </div>`,
        timer: 2500,
        showConfirmButton: false,
      });
      if (refetch) refetch();
      onClose();
    } catch (err) {
      setLoading(false);
      swalAbove.fire({
        icon: "error",
        title: "Recharge Failed",
        text: err.response?.data?.message || "Something went wrong",
      });
    }
  };

  // ── Tiny inline icons ─────────────────────────────────────────────────────
  const CoinIcon = ({ color = "#d97706", size = 14 }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      style={{ flexShrink: 0 }}
    >
      <circle cx="12" cy="12" r="9" stroke={color} strokeWidth="1.8" />
      <text
        x="12"
        y="16"
        textAnchor="middle"
        fontSize="9"
        fontWeight="700"
        fill={color}
      >
        C
      </text>
    </svg>
  );

  // ── Wallet badge ──────────────────────────────────────────────────────────
  const WalletBadge = () => {
    const base = "flex items-center gap-2 rounded-xl border px-3 py-1.5";
    if (walletLoading)
      return (
        <div className={`${base} border-blue-200 bg-blue-50`}>
          <CoinIcon color="#3b82f6" />
          <div className="flex flex-col leading-none">
            <span className="text-[9px] font-semibold uppercase tracking-widest text-blue-400">
              Coin Balance
            </span>
            <div className="flex gap-1 pt-0.5">
              {[0, 150, 300].map((d) => (
                <span
                  key={d}
                  className="h-1.5 w-1.5 animate-bounce rounded-full bg-blue-400"
                  style={{ animationDelay: `${d}ms` }}
                />
              ))}
            </div>
          </div>
        </div>
      );
    if (walletBalance === null)
      return (
        <div className={`${base} border-gray-200 bg-gray-50`}>
          <CoinIcon color="#9ca3af" />
          <div className="flex flex-col leading-none">
            <span className="text-[9px] font-semibold uppercase tracking-widest text-gray-400">
              Coin Balance
            </span>
            <span className="text-sm font-semibold text-gray-400">—</span>
          </div>
        </div>
      );
    return (
      <div className={`${base} border-emerald-200 bg-emerald-50`}>
        <CoinIcon color="#059669" />
        <div className="flex flex-col leading-none">
          <span className="text-emerald-500 text-[9px] font-semibold uppercase tracking-widest">
            Coin Balance
          </span>
          <span className="text-emerald-700 text-sm font-semibold">
            {walletBalance.toLocaleString("en-IN")} Coins
          </span>
        </div>
      </div>
    );
  };

  return (
    <form onSubmit={handleMakePayment} className="w-full space-y-5">
      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold sm:text-xl lg:text-2xl">
          Manual Recharge
        </h3>
        <div className="flex items-center gap-3">
          {shouldShowWallet && <WalletBadge />}
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-500 hover:bg-gray-100"
          >
            <MdClose className="text-xl" />
          </button>
        </div>
      </div>

      {/* ── Form Grid ── */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {/* User */}
        <div>
          <label className="text-sm font-medium text-gray-700">User</label>
          <div className="mt-1">
            {selectedUser ? (
              <div className="flex w-full items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                <div>
                  <p className="text-sm font-semibold text-gray-800">
                    {selectedUser.fullName}
                  </p>
                  <p className="text-xs text-gray-500">{selectedUser.phone}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedUser(null)}
                  className="rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-600"
                >
                  <MdClose size={16} />
                </button>
              </div>
            ) : (
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setErrors((p) => ({ ...p, user: "" }));
                  }}
                  placeholder="Enter full name or phone number"
                  className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300"
                />
                {/* Dropdown: results or no-match */}
                {searchQuery.trim().length > 0 && searchDone && (
                  <div className="absolute left-0 top-full z-20 mt-1 w-full overflow-hidden rounded-lg border bg-white shadow-lg">
                    {suggestions.length > 0 ? (
                      <ul className="max-h-44 overflow-auto">
                        {suggestions.map((s) => (
                          <li
                            key={s._id}
                            onClick={() => handleSelectSuggestion(s)}
                            className="flex cursor-pointer items-center justify-between px-3 py-2.5 hover:bg-pink-50"
                          >
                            <div>
                              <p className="text-sm font-medium text-gray-800">
                                {s.fullName}
                              </p>
                              <p className="text-xs text-gray-500">{s.phone}</p>
                            </div>
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <path
                                d="M9 18l6-6-6-6"
                                stroke="#9ca3af"
                                strokeWidth="2"
                                strokeLinecap="round"
                              />
                            </svg>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="flex items-center gap-2 px-4 py-3 text-sm text-gray-500">
                        <svg
                          width="15"
                          height="15"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <circle
                            cx="11"
                            cy="11"
                            r="7"
                            stroke="#9ca3af"
                            strokeWidth="1.8"
                          />
                          <path
                            d="M16.5 16.5L21 21"
                            stroke="#9ca3af"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                          />
                          <path
                            d="M11 8v3m0 3h.01"
                            stroke="#9ca3af"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                          />
                        </svg>
                        No user found for{" "}
                        <b className="ml-1">"{searchQuery.trim()}"</b>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          {errors.user && (
            <p className="mt-1 text-xs text-red-500">{errors.user}</p>
          )}
        </div>

        {/* Recharge Type */}
        <div>
          <label className="text-sm font-medium text-gray-700">
            Recharge Type
          </label>
          <select
            value={rechargeType}
            onChange={(e) => {
              setRechargeType(e.target.value);
              setErrors((p) => ({ ...p, rechargeType: "" }));
            }}
            className="mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300"
          >
            <option value="">Select Recharge Type</option>
            <option value="manual">Manual</option>
            <option value="cashfreefailed">Cashfree Failed</option>
          </select>
          {errors.rechargeType && (
            <p className="mt-1 text-xs text-red-500">{errors.rechargeType}</p>
          )}
          {rechargeType === "manual" && (
            <input
              value={utr}
              onChange={(e) => {
                setUtr(e.target.value);
                setErrors((p) => ({ ...p, utr: "" }));
              }}
              placeholder="Enter UTR"
              className="mt-2 w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300"
            />
          )}
          {(rechargeType === "cashfree" ||
            rechargeType === "cashfreefailed") && (
            <input
              value={transactionId}
              onChange={(e) => {
                setTransactionId(e.target.value);
                setErrors((p) => ({ ...p, transactionId: "" }));
              }}
              placeholder="Cashfree Transaction ID"
              className="mt-2 w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300"
            />
          )}
          {errors.utr && (
            <p className="mt-1 text-xs text-red-500">{errors.utr}</p>
          )}
          {errors.transactionId && (
            <p className="mt-1 text-xs text-red-500">{errors.transactionId}</p>
          )}
        </div>

        {/* Coin Plan */}
        <div className="relative">
          <label className="text-sm font-medium text-gray-700">Coin Plan</label>
          <input
            type="text"
            value={coinPlanSearch}
            onChange={(e) => {
              setCoinPlanSearch(e.target.value);
              setShowCoinPlanDropdown(true);
              setErrors((p) => ({ ...p, coinPlan: "" }));
            }}
            onFocus={() => setShowCoinPlanDropdown(true)}
            onBlur={() => setTimeout(() => setShowCoinPlanDropdown(false), 200)}
            placeholder="Search by coin amount..."
            className="mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300"
          />
          {showCoinPlanDropdown && filteredCoinPlans.length > 0 && (
            <ul className="absolute left-0 top-full z-20 mt-1 max-h-44 w-full overflow-auto rounded-lg border bg-white shadow-lg">
              {filteredCoinPlans.map((p) => (
                <li
                  key={p._id}
                  onMouseDown={() => handleCoinPlanSelect(p)}
                  className="flex cursor-pointer items-center justify-between px-3 py-2.5 hover:bg-pink-50"
                >
                  <span className="text-sm font-semibold text-gray-800">
                    {p.coinAmount} Coins
                  </span>
                  <span className="text-xs text-gray-500">
                    ₹{p.purchaseAmount}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {errors.coinPlan && (
            <p className="mt-1 text-xs text-red-500">{errors.coinPlan}</p>
          )}
        </div>

        {/* Promo Code */}
        <div className="relative">
          <label className="text-sm font-medium text-gray-700">
            Promo Code{" "}
            <span className="font-normal text-gray-400">(optional)</span>
          </label>
          <input
            type="text"
            value={discountSearch}
            onChange={(e) => {
              setDiscountSearch(e.target.value);
              setShowDiscountDropdown(true);
            }}
            onFocus={() => setShowDiscountDropdown(true)}
            onBlur={() => setTimeout(() => setShowDiscountDropdown(false), 200)}
            placeholder="Search promo code..."
            className="mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300"
          />
          {showDiscountDropdown && filteredDiscounts.length > 0 && (
            <ul className="absolute left-0 top-full z-20 mt-1 max-h-44 w-full overflow-auto rounded-lg border bg-white shadow-lg">
              {filteredDiscounts.map((p) => (
                <li
                  key={p._id}
                  onMouseDown={() => handleDiscountSelect(p)}
                  className="flex cursor-pointer items-center justify-between px-3 py-2.5 hover:bg-pink-50"
                >
                  <span className="text-sm font-medium text-gray-800">
                    {p.code}
                  </span>
                  <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">
                    {p.discount}% OFF
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Discount */}
        <div>
          <label className="text-sm font-medium text-gray-700">
            Manual Discount{" "}
            <span className="font-normal text-gray-400">(optional)</span>
          </label>
          <div className="mt-1 flex items-center gap-4">
            <label className="flex cursor-pointer items-center gap-1.5 text-sm text-gray-600">
              <input
                type="radio"
                name="discountType"
                value="amount"
                checked={discountType === "amount"}
                onChange={() => {
                  setDiscountType("amount");
                  setDiscountValue("");
                  setDiscount(null);
                  setDiscountSearch("");
                }}
              />{" "}
              ₹ Amount
            </label>
            <label className="flex cursor-pointer items-center gap-1.5 text-sm text-gray-600">
              <input
                type="radio"
                name="discountType"
                value="percent"
                checked={discountType === "percent"}
                onChange={() => {
                  setDiscountType("percent");
                  setDiscountValue("");
                  setDiscount(null);
                  setDiscountSearch("");
                }}
              />{" "}
              % Percent
            </label>
          </div>
          <input
            type="number"
            value={discountValue}
            disabled={!discountType}
            onChange={(e) => setDiscountValue(Math.max(0, e.target.value))}
            placeholder={
              !discountType
                ? "Select type above first"
                : discountType === "amount"
                ? "Enter ₹ amount"
                : "Enter %"
            }
            className={`mt-2 w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300 ${
              !discountType
                ? "cursor-not-allowed bg-gray-100 text-gray-400"
                : ""
            }`}
          />
        </div>

        {/* Recharge By */}
        <div>
          <label className="text-sm font-medium text-gray-700">
            Recharge By
          </label>
          <input
            type="text"
            value={rechargeBy}
            readOnly
            className="mt-1 w-full rounded-lg border bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700"
          />
        </div>

        {/* Method */}
        <div>
          <label className="text-sm font-medium text-gray-700">
            Payment Method
          </label>
          <select
            value={method}
            onChange={(e) => {
              setMethod(e.target.value);
              setErrors((p) => ({ ...p, method: "" }));
            }}
            className="mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300"
            disabled={!rechargeType}
          >
            <option value="">
              {rechargeType ? "Select method" : "Select recharge type first"}
            </option>
            {availableMethods.map((m, i) => (
              <option key={i} value={m}>
                {m}
              </option>
            ))}
          </select>
          {errors.method && (
            <p className="mt-1 text-xs text-red-500">{errors.method}</p>
          )}
          {method === "Other" && (
            <input
              type="text"
              value={customMethod}
              onChange={(e) => {
                setCustomMethod(e.target.value);
                setErrors((p) => ({ ...p, customMethod: "" }));
              }}
              placeholder="Specify method"
              className="mt-2 w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300"
            />
          )}
          {errors.customMethod && (
            <p className="mt-1 text-xs text-red-500">{errors.customMethod}</p>
          )}
        </div>
      </div>

      {/* ── Payment Summary ─────────────────────────────────────────────────── */}
      <div className="w-full md:w-7/12">
        {coinPlan ? (
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
            {/* Header */}
            <div className="border-b border-gray-100 bg-gray-50 px-4 py-2.5">
              <span className="text-xs font-semibold uppercase tracking-widest text-gray-500">
                Payment Summary
              </span>
            </div>

            <div className="divide-y divide-gray-100 px-4">
              {/* Coins to user */}
              <div className="flex items-center justify-between py-2.5">
                <span className="flex items-center gap-1.5 text-sm text-gray-600">
                  <CoinIcon color="#d97706" /> Coins to user
                </span>
                <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-sm font-bold text-amber-700">
                  {coinsToCredit.toLocaleString("en-IN")} Coins
                </span>
              </div>

              {/* Plan price */}
              <div className="flex items-center justify-between py-2.5">
                <span className="text-sm text-gray-500">Plan price</span>
                <span className="text-sm font-medium text-gray-700">
                  ₹{coinPlan.purchaseAmount.toLocaleString("en-IN")}
                </span>
              </div>

              {/* Discount row (only if applied) */}
              {discountedRupees > 0 && (
                <div className="flex items-center justify-between py-2.5">
                  <span className="flex items-center gap-1.5 text-sm text-gray-500">
                    Discount
                    {discount && !discountType && (
                      <span className="rounded bg-green-100 px-1.5 py-0.5 text-[10px] font-semibold text-green-700">
                        PROMO
                      </span>
                    )}
                    {discountType === "percent" && discountValue > 0 && (
                      <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700">
                        {discountValue}% OFF
                      </span>
                    )}
                    {discountType === "amount" && discountValue > 0 && (
                      <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700">
                        FLAT ₹{discountValue}
                      </span>
                    )}
                  </span>
                  <span className="text-sm font-medium text-green-600">
                    −₹{discountedRupees.toLocaleString("en-IN")}
                  </span>
                </div>
              )}

              {/* Final amount to pay */}
              <div className="-mx-4 flex items-center justify-between bg-blue-50 px-4 py-3">
                <span className="text-sm font-semibold text-blue-800">
                  Amount to Pay
                </span>
                <span className="text-base font-bold text-blue-700">
                  ₹{finalPurchaseAmount.toLocaleString("en-IN")}
                </span>
              </div>

              {/* Wallet section for non-admin staff only */}
              {shouldShowWallet && walletBalance !== null && (
                <>
                  <div className="flex items-center justify-between py-2.5">
                    <span className="flex items-center gap-1.5 text-sm text-gray-500">
                      <CoinIcon color="#9333ea" /> Coins deducted from wallet
                    </span>
                    <span className="text-sm font-semibold text-purple-600">
                      −{coinDeduction.toLocaleString("en-IN")} Coins
                    </span>
                  </div>

                  <div className="flex items-center justify-between py-2.5">
                    <span className="flex items-center gap-1.5 text-sm text-gray-500">
                      <CoinIcon
                        color={isInsufficientBalance ? "#dc2626" : "#059669"}
                      />
                      Remaining balance
                    </span>
                    {isInsufficientBalance ? (
                      <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-sm font-bold text-red-600">
                        Only {walletBalance.toLocaleString("en-IN")} Coins left
                      </span>
                    ) : (
                      <span className="bg-emerald-100 text-emerald-700 rounded-full px-2.5 py-0.5 text-sm font-bold">
                        {remainingCoinBalance.toLocaleString("en-IN")} Coins
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Insufficient balance warning */}
            {shouldShowWallet && isInsufficientBalance && (
              <div className="mx-4 mb-3 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
                <span className="shrink-0">⚠️</span>
                <span>
                  Insufficient coins. You have{" "}
                  <b>{walletBalance.toLocaleString("en-IN")}</b> but need{" "}
                  <b>{coinDeduction.toLocaleString("en-IN")}</b> coins. Contact
                  admin.
                </span>
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2.5 rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-3 text-sm text-gray-400">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <circle
                cx="12"
                cy="12"
                r="9"
                stroke="#d1d5db"
                strokeWidth="1.8"
              />
              <path
                d="M12 8v4m0 4h.01"
                stroke="#d1d5db"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
            Select a coin plan to see the payment summary
          </div>
        )}
      </div>

      {/* ── Actions ── */}
      <div className="flex flex-col justify-end gap-3 sm:flex-row">
        <button
          type="button"
          onClick={onClose}
          disabled={loading}
          className="w-full rounded-lg border bg-gray-200 px-5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300 sm:w-auto"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading || isInsufficientBalance}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#c51369] px-5 py-2 text-sm font-medium text-white hover:bg-[#a10f5b] disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
        >
          {loading ? <BtnLoader /> : "Make Payment"}
        </button>
      </div>
    </form>
  );
};

export default ManualRechargeForm;
